// Dedicated Pets page — a quick overview of every pet in the family,
// with today's walks, feeds, and water changes in one scrollable column.
// Designed for families with more than one animal (cat + dog + rabbit etc.)
// who'd otherwise have to tap each member card on the dashboard to see the
// day's activity.

import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Footprints, UtensilsCrossed, Droplets } from "lucide-react";
import { petSpeciesMeta, petWalkVibe, petCareVibe } from "@/lib/vibe";
import { isYoungKid } from "@/lib/birthdays";
import PetCareExtras from "@/components/PetCareExtras";
import { api } from "@/lib/api";
import { fmtTime } from "@/lib/time";
import { isPetWalkLockedForPet } from "@/lib/petCareGate";

function fmtHM(iso) {
  return fmtTime(iso) || "—";
}

function relHrs(iso) {
  if (!iso) return null;
  const hrs = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  if (hrs < 0.5) return "just now";
  if (hrs < 1) return "under an hour ago";
  if (hrs < 24) return `${Math.round(hrs)}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function SummaryTile({ icon: Icon, label, value, sub, tint, testid }) {
  return (
    <div
      className="ff-card p-3 flex flex-col gap-1"
      style={tint ? { background: tint } : undefined}
      data-testid={testid}
    >
      <div className="label-tiny flex items-center gap-1.5">
        <Icon className="w-3 h-3" strokeWidth={2} />
        {label}
      </div>
      <div className="font-display text-2xl leading-none tabular tracking-tighter">
        {value}
      </div>
      {sub && (
        <div className="text-[12px] text-[color:var(--ink-2)] leading-tight">
          {sub}
        </div>
      )}
    </div>
  );
}

function PetRow({ pet, checkin, members, tier, viewerIsYoungKid, onOpen, onPetUpdated, onOpenUpgrade }) {
  const navigate = useNavigate();
  const isDog = pet.pet_species === "dog" || (!pet.pet_species && (pet.name || "").toLowerCase().includes("dog"));
  const species = pet.pet_species || (isDog ? "dog" : "other");
  const meta = petSpeciesMeta(species);
  const walks = (checkin?.walks || []).slice().sort((a, b) => (a.at > b.at ? -1 : 1));
  const fedAt = checkin?.fed_at;
  const waterAt = checkin?.water_change_at;
  const fedBy = checkin?.fed_by
    ? members.find((m) => m.id === checkin.fed_by)?.name
    : checkin?.fed_by_label;
  const waterBy = checkin?.water_change_by
    ? members.find((m) => m.id === checkin.water_change_by)?.name
    : checkin?.water_change_by_label;
  const vibe = isDog ? petWalkVibe(checkin) : petCareVibe(checkin, species);
  const next = checkin?.next_walk_at;
  const nextBy = checkin?.next_walk_by
    ? members.find((m) => m.id === checkin.next_walk_by)?.name
    : checkin?.next_walk_by_label;
  const walkLocked = isPetWalkLockedForPet(pet, members, tier);

  return (
    <section
      className="ff-card ff-card-hover p-5 md:p-6 text-left cursor-pointer"
      style={{ background: vibe?.tint || "var(--surface)" }}
      data-testid={`pet-row-${pet.id}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen && onOpen(pet)}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && onOpen) {
          e.preventDefault();
          onOpen(pet);
        }
      }}
    >
      {/* Header — avatar + name + species + mood */}
      <div className="flex items-center gap-3 mb-4">
        <span className="w-12 h-12 rounded-full bg-[color:var(--bg)] border border-[color:var(--surface)] flex items-center justify-center text-2xl overflow-hidden shrink-0">
          {pet.avatar_image ? (
            <img
              src={pet.avatar_image}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            pet.avatar_emoji || meta.emoji
          )}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-display-italic text-2xl leading-tight tracking-tight">
            {pet.name}
          </div>
          <div className="label-tiny text-[color:var(--ink-2)]">
            {meta.label}
            {checkin?.mood && !isDog && ` · mood ${checkin.mood}/10`}
          </div>
          {walkLocked && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenUpgrade && onOpenUpgrade();
              }}
              className="ff-chip text-[11px] mt-1 !py-0.5 !bg-[color:var(--ink-3)]/10 !border-[color:var(--ink-3)]/30 !text-[color:var(--ink-2)] hover:!bg-[color:var(--ink-3)]/20"
              data-testid={`pet-row-walk-locked-${pet.id}`}
              title="Walk logging is on the upgrade list — tap to upgrade"
            >
              🔒 Upgrade to log walks
            </button>
          )}
        </div>
        {/* Direct History → link so the family doesn't need to open the
            care dialog just to scroll back. Dogs and cats only — other
            species don't have a per-day care log worth scrolling. */}
        {(species === "dog" || species === "cat") && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/pets/history/${pet.id}`);
            }}
            className="ff-chip text-[12px] shrink-0 !bg-[#EADFCB] !border-[#D4C4A8] !text-[#5A4A2E] hover:!bg-[#DECDB2]"
            data-testid={`pet-row-history-${pet.id}`}
            title={`View ${pet.name}'s care history`}
          >
            History →
          </button>
        )}
      </div>

      {/* Summary tiles — adapt to species (dogs: walks+water; others: fed+water+mood) */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
        {isDog && (
          <SummaryTile
            icon={Footprints}
            label="Walks today"
            value={walks.length}
            sub={
              walks[0]
                ? `Last ${fmtHM(walks[0].at)}`
                : "No walks yet"
            }
            testid={`pet-summary-walks-${pet.id}`}
          />
        )}
        {isDog && (
          <SummaryTile
            icon={UtensilsCrossed}
            label="Fed today"
            value={fedAt ? fmtHM(fedAt) : "—"}
            sub={fedAt ? `${relHrs(fedAt)}${fedBy ? ` · ${fedBy}` : ""}` : "Not yet"}
            testid={`pet-summary-fed-dog-${pet.id}`}
          />
        )}
        {!isDog && (
          <SummaryTile
            icon={Droplets}
            label="Water"
            value={fmtHM(waterAt)}
            sub={waterAt ? `${relHrs(waterAt)}${waterBy ? ` · ${waterBy}` : ""}` : "Not yet"}
            testid={`pet-summary-water-${pet.id}`}
          />
        )}
        {!isDog && meta.fedHrs !== null && (
          <SummaryTile
            icon={UtensilsCrossed}
            label="Fed"
            value={fmtHM(fedAt)}
            sub={fedAt ? `${relHrs(fedAt)}${fedBy ? ` · ${fedBy}` : ""}` : "Not yet"}
            testid={`pet-summary-fed-${pet.id}`}
          />
        )}
        {isDog && next && (
          <SummaryTile
            icon={Footprints}
            label="Next walk"
            value={fmtHM(next)}
            sub={nextBy ? `by ${nextBy}` : undefined}
            testid={`pet-summary-next-${pet.id}`}
          />
        )}
        {isDog && (
          <SummaryTile
            icon={Droplets}
            label="Water refilled"
            value={waterAt ? fmtHM(waterAt) : "—"}
            sub={waterAt ? `${relHrs(waterAt)}${waterBy ? ` · ${waterBy}` : ""}` : "Not yet"}
            testid={`pet-summary-water-dog-${pet.id}`}
          />
        )}
      </div>

      {/* Full walk log */}
      {isDog && walks.length > 0 && (
        <div className="mt-4">
          <div className="label-tiny mb-2">Today's walks</div>
          <ul className="space-y-1">
            {walks.map((w, i) => {
              const by = w.by ? members.find((m) => m.id === w.by) : null;
              const name = by?.name || w.by_label || "—";
              return (
                <li
                  key={`${w.at}-${i}`}
                  className="flex items-baseline gap-3 text-[15px]"
                >
                  <span className="tabular font-mono text-[13px] w-12 text-[color:var(--ink-2)]">
                    {fmtHM(w.at)}
                  </span>
                  <span className="text-[color:var(--ink)]">by {name}</span>
                  {w.origin === "walk-chore" && (
                    <span className="label-tiny text-[color:var(--ink-3)]">
                      · from chore
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Non-dog pets with additional feed entries */}
      {!isDog && (checkin?.feeds || []).length > 1 && (
        <div className="mt-4">
          <div className="label-tiny mb-2">Today's feeds</div>
          <ul className="space-y-1">
            {(checkin.feeds || []).map((f, i) => (
              <li key={`${f.at}-${i}`} className="text-[15px] flex items-baseline gap-3">
                <span className="tabular font-mono text-[13px] w-12 text-[color:var(--ink-2)]">
                  {fmtHM(f.at)}
                </span>
                <span>
                  by {f.by ? members.find((m) => m.id === f.by)?.name || "—" : f.by_label || "—"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* "Pet nerd" care log: vet exam, tick/flea pill, bath, litter.
          Dogs + cats only; fish/birds/reptiles are intentionally plain. */}
      <PetCareExtras
        pet={pet}
        members={members}
        tier={tier}
        viewerIsYoungKid={viewerIsYoungKid}
        onPetUpdated={onPetUpdated}
        onOpenUpgrade={onOpenUpgrade}
      />
    </section>
  );
}

export default function PetsPage({ members: initialMembers, checkins, viewerId, onBack, onOpenUpgrade }) {
  const navigate = useNavigate();
  const [members, setMembers] = React.useState(initialMembers || []);
  const [tier, setTier] = React.useState(null);
  React.useEffect(() => {
    setMembers(initialMembers || []);
  }, [initialMembers]);

  // If the active viewer (the person who picked their card on the
  // kitchen iPad) is a young kid (≤ 7), lock the pet Care log so they
  // can't accidentally log a vet visit or a bath. Walks / feeds /
  // water are still open because those are the everyday "help the pet"
  // actions young kids love to do.
  const viewerIsYoungKid = useMemo(() => {
    if (!viewerId) return false;
    const v = (initialMembers || []).find((m) => m.id === viewerId);
    return !!v && isYoungKid(v);
  }, [viewerId, initialMembers]);

  // Fetch current subscription tier so the Care log rows know whether to
  // render the full editor or a locked preview with an upgrade CTA.
  // Loaded once on mount — tier changes are rare and Dashboard refetches
  // after a successful Stripe return, so we don't need to poll here.
  React.useEffect(() => {
    let alive = true;
    api
      .billingStatus()
      .then((s) => {
        if (alive) setTier(s?.tier || "free");
      })
      .catch(() => {
        if (alive) setTier("free");
      });
    return () => {
      alive = false;
    };
  }, []);

  // When a pet doc changes (care milestone logged, interval edited), swap
  // it in locally so the UI updates immediately — no need to refetch the
  // whole family. The next full refresh from Dashboard will still win
  // eventually if something changed server-side.
  const onPetUpdated = (updated) => {
    if (!updated?.id) return;
    setMembers((cur) => cur.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
  };
  const pets = useMemo(
    () => (members || []).filter((m) => m.role === "pet"),
    [members]
  );
  const checkinByMember = useMemo(
    () => Object.fromEntries((checkins || []).map((c) => [c.member_id, c])),
    [checkins]
  );

  // Family-wide counters across all pets — a glanceable hero.
  const totals = useMemo(() => {
    let walks = 0, feeds = 0, waters = 0;
    for (const p of pets) {
      const c = checkinByMember[p.id];
      if (!c) continue;
      walks += (c.walks || []).length;
      feeds += (c.feeds || []).length + (c.fed_at ? 1 : 0);
      waters += (c.water_changes || []).length + (c.water_change_at ? 1 : 0);
    }
    return { walks, feeds, waters };
  }, [pets, checkinByMember]);

  const handleBack = () => {
    if (onBack) onBack();
    else navigate("/app");
  };

  if (pets.length === 0) {
    return (
      <div className="min-h-screen px-6 py-12 flex items-center justify-center" data-testid="pets-page-empty">
        <div className="max-w-sm text-center space-y-4">
          <div className="text-5xl">🐾</div>
          <div className="font-display-italic text-2xl">No pets yet</div>
          <p className="text-[color:var(--ink-2)] text-sm">
            Add a pet from the dashboard to start tracking walks, feeds and water changes.
          </p>
          <button onClick={handleBack} className="ff-btn" data-testid="pets-back-empty">
            Back to family
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="pets-page">
      <header className="px-4 md:px-10 pt-6 md:pt-10 pb-6">
        <div className="max-w-4xl mx-auto flex items-start justify-between gap-3">
          <div>
            <button
              onClick={handleBack}
              className="label-tiny text-[color:var(--ink-3)] hover:text-[color:var(--ink)] inline-flex items-center gap-1 mb-3"
              data-testid="pets-back"
            >
              <ArrowLeft className="w-3 h-3" strokeWidth={2} />
              Family
            </button>
            <h1 className="font-display-italic text-3xl md:text-5xl leading-[1.05] tracking-tight">
              {pets.length === 1 ? "Your pet today" : `Your ${pets.length} pets today`}
            </h1>
          </div>
        </div>
      </header>

      <div className="px-4 md:px-10 pb-6">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-3" data-testid="pets-totals">
          <SummaryTile
            icon={Footprints}
            label="Walks"
            value={totals.walks}
            sub={totals.walks === 0 ? "Not yet today" : null}
          />
          <SummaryTile
            icon={UtensilsCrossed}
            label="Feeds"
            value={totals.feeds}
            sub={totals.feeds === 0 ? "Not yet today" : null}
          />
          <SummaryTile
            icon={Droplets}
            label="Water"
            value={totals.waters}
            sub={totals.waters === 0 ? "Not yet today" : null}
          />
        </div>
      </div>

      <main className="px-4 md:px-10 pb-16">
        <div className="max-w-4xl mx-auto space-y-5">
          {pets.map((p) => (
            <PetRow
              key={p.id}
              pet={p}
              checkin={checkinByMember[p.id]}
              members={members}
              tier={tier}
              viewerIsYoungKid={viewerIsYoungKid}
              onPetUpdated={onPetUpdated}
              onOpenUpgrade={onOpenUpgrade}
              onOpen={(pet) => {
                try {
                  sessionStorage.setItem("ff_pending_checkin", pet.id);
                } catch {}
                navigate("/app");
              }}
            />
          ))}
        </div>
      </main>
    </div>
  );
}
